import { Component } from '@theme/component';
import { debounce, onDocumentLoaded } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

const ACTIVATE_DELAY = 0;
const DEACTIVATE_DELAY = 350;

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 * @property {HTMLElement | null} overflowTrigger - The visible More control for an active overflow item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    const overflowMenu = this.overflowMenu;
    if (overflowMenu) {
      overflowMenu.inert = true;
      overflowMenu.addEventListener('pointerleave', () => this.#debouncedDeactivate(), {
        signal: this.#abortController.signal,
      });
    }
    this.addEventListener('keydown', this.#onKeydown, { signal: this.#abortController.signal });

    onDocumentLoaded(this.#preloadImages);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
    overflowTrigger: null,
  };

  /** Close an open submenu and restore focus to its disclosure control. */
  #onKeydown = (event) => {
    if (event.key !== 'Escape' || !this.#state.activeItem) return;

    event.preventDefault();
    const focusTarget = this.#focusTargetAfterClose(this.#state.activeItem);
    this.#deactivate(this.#state.activeItem, { force: true });
    focusTarget.focus();
  };

  /** Toggle a submenu from its dedicated disclosure button. */
  toggle = (event) => {
    if (!(event.target instanceof Element)) return;

    const item = findMenuItem(event.target);
    if (!item) return;

    const disclosure = this.#expandableFor(item);
    if (disclosure.ariaExpanded === 'true') {
      this.#deactivate(this.#state.activeItem ?? item, { force: true });
      this.#focusTargetAfterClose(item).focus();
      return;
    }

    this.activate(event);
  };

  /** @param {HTMLElement} item */
  #expandableFor(item) {
    const disclosure = item.closest('.menu-list__list-item')?.querySelector('[ref="disclosure[]"]');
    return disclosure instanceof HTMLElement ? disclosure : item;
  }

  /** @param {HTMLElement} item */
  #focusTargetAfterClose(item) {
    const listItem = item.closest('.menu-list__list-item');
    if (listItem instanceof HTMLElement && listItem.slot === 'overflow') {
      const moreButton = this.querySelector('[slot="more"] [ref="menuitem"]');
      if (moreButton instanceof HTMLElement) return moreButton;
    }
    return this.#expandableFor(item);
  }

  /**
   * Time to allow for a closing animation between initiating a deactivation and actually deactivating the active item.
   * @returns {number}
   */
  get animationDelay() {
    const value = this.dataset.animationDelay;
    return value ? parseInt(value, 10) : 0;
  }

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow menu is hovered
   * @returns {boolean}
   */
  get overflowHovered() {
    return this.refs.overflowMenu?.matches(':hover') ?? false;
  }

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    this.#debouncedDeactivate.cancel();
    this.#debouncedActivateHandler.cancel();

    this.#debouncedActivateHandler(event);
  };

  /**
   * Activate the selected menu item with a delay
   * @param {PointerEvent | FocusEvent} event
   */
  #activateHandler = (event) => {
    this.#debouncedDeactivate.cancel();

    this.dispatchEvent(new MegaMenuHoverEvent());

    this.removeAttribute('data-animating');

    if (!(event.target instanceof Element)) return;

    let item = findMenuItem(event.target);

    if (!item || item == this.#state.activeItem) return;

    const listItem = item.closest('.menu-list__list-item');
    const isDefaultSlot = !(listItem instanceof HTMLElement) || listItem.slot === '';
    const overflowTrigger = isDefaultSlot
      ? null
      : this.querySelector('[slot="more"] [ref="menuitem"]');

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;
    const previousOverflowTrigger = this.#state.overflowTrigger;

    if (previouslyActiveItem) {
      this.#expandableFor(previouslyActiveItem).ariaExpanded = 'false';
      const previousSubmenu = findSubmenu(previouslyActiveItem);
      if (previousSubmenu) {
        previousSubmenu.inert = true;
        delete previousSubmenu.dataset.active;
      }
    }
    if (previousOverflowTrigger && previousOverflowTrigger !== overflowTrigger) {
      previousOverflowTrigger.ariaExpanded = 'false';
      if (this.overflowMenu) {
        this.overflowMenu.inert = true;
        delete this.overflowMenu.dataset.active;
      }
    }

    let submenu = findSubmenu(item);
    if (!submenu && !isDefaultSlot) submenu = this.overflowMenu;

    // Plain navigation links do not control a popup and must never expose a
    // misleading aria-expanded state.
    if (!submenu) {
      this.#state.activeItem = null;
      this.#state.overflowTrigger = null;
      this.ariaExpanded = 'false';
      this.dataset.overflowExpanded = 'false';
      this.style.setProperty('--submenu-height', '0px');
      this.style.setProperty('--submenu-opacity', '0');
      return;
    }

    this.#state.activeItem = item;
    this.#state.overflowTrigger = overflowTrigger instanceof HTMLElement ? overflowTrigger : null;
    this.ariaExpanded = 'true';
    this.#expandableFor(item).ariaExpanded = 'true';
    if (this.#state.overflowTrigger) this.#state.overflowTrigger.ariaExpanded = 'true';

    // Mark submenu as active for content-visibility optimization
    if (submenu) {
      submenu.dataset.active = '';
      submenu.inert = false;
    }
    if (!isDefaultSlot && this.overflowMenu) {
      this.overflowMenu.dataset.active = '';
      this.overflowMenu.inert = false;
    }

    const submenuHeight = submenu ? submenu.offsetHeight : 0;

    this.style.setProperty('--submenu-height', `${submenuHeight}px`);
    this.style.setProperty('--submenu-opacity', '1');
  };

  #debouncedActivateHandler = debounce(this.#activateHandler, ACTIVATE_DELAY);

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    this.#debouncedActivateHandler.cancel();

    if (!(event.target instanceof Element)) return;

    const item = findMenuItem(event.target);
    const listItem = this.#state.activeItem?.closest('.menu-list__list-item');

    if (event.relatedTarget instanceof Node && listItem?.contains(event.relatedTarget)) return;

    // Make sure the item to be deactivated is still the active one. Ideally
    // we cancelled the debounce before the item was changed, but just in case.
    if (item === this.#state.activeItem) {
      this.#debouncedDeactivate();
    }
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem, { force = false } = {}) => {
    if (!item || item != this.#state.activeItem) return;
    if (!force && this.overflowHovered) return;

    this.style.setProperty('--submenu-height', '0px');
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    const overflowContext = item.closest('[slot="more"], [slot="overflow"]') ? this.overflowMenu : null;
    const submenu = findSubmenu(item) ?? overflowContext;
    const overflowTrigger = this.#state.overflowTrigger;

    this.#state.activeItem = null;
    this.#state.overflowTrigger = null;
    this.ariaExpanded = 'false';
    this.#expandableFor(item).ariaExpanded = 'false';
    if (overflowTrigger) overflowTrigger.ariaExpanded = 'false';
    item.setAttribute('data-animating', '');

    if (submenu) submenu.inert = true;
    if (overflowContext) overflowContext.inert = true;

    setTimeout(() => {
      item.removeAttribute('data-animating');
      // Remove active state from submenu after animation completes
      if (submenu) {
        delete submenu.dataset.active;
      }
      if (overflowContext && overflowContext !== submenu) {
        delete overflowContext.dataset.active;
      }
    }, Math.max(0, this.animationDelay - 150)); // Start header transition 150ms before submenu finishes
  };

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  #debouncedDeactivate = debounce(this.#deactivate, DEACTIVATE_DELAY);

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  const listItem = element.closest('.menu-list__list-item') ?? element;
  if (listItem instanceof HTMLElement && listItem.slot === 'more') {
    const firstOverflowItem = listItem.parentElement?.querySelector('[slot="overflow"] [ref="menuitem"]');
    if (firstOverflowItem instanceof HTMLElement) return firstOverflowItem;
  }
  const item = listItem.querySelector('[ref="menuitem"]');
  return item instanceof HTMLElement ? item : null;
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const listItem = element?.closest?.('.menu-list__list-item');
  const submenu = listItem?.querySelector(':scope > [ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
